import { inject, Injectable } from '@angular/core'

import { Observable } from 'rxjs'

import { AuditAction, AuditCategory, AuditLog, DataQueryProps, UserProfile } from '@ti/contracts'
import { ExportFormat } from '@ti/core/common/contracts'
import { formatPhoneNumber } from '@ti/core/ui-kit/i18n'
import { ActionData } from '@ti/shared/audit/contracts'
import { AuditLogGridDataResolver } from '@ti/shared/audit/services'
import { formatAddress } from '@ti/shared/audit/utils'

import { TiAdminUsersStore } from '../../common/stores'

@Injectable()
export class AdminAuditLogGridDataResolver extends AuditLogGridDataResolver {
  public readonly SUPPORTED_CATEGORIES: AuditCategory[] = [
    AuditCategory.TSPAccounts,
    AuditCategory.SystemControls,
    AuditCategory.Roles,
    AuditCategory.Users,
  ]
  protected readonly usersStore = inject(TiAdminUsersStore)

  public override async getActionKey(row: AuditLog) {
    // Override category for TI Admin to use custom translations
    const category =
      row.category === AuditCategory.Users ? ('ti-admin-users' as AuditCategory) : row.category

    return super.getActionKey({ ...row, category })
  }

  public override async getActionData(row: AuditLog): Promise<ActionData> {
    const actionData = await super.getActionData(row)
    if (row.category === AuditCategory.Commands) {
      actionData.isSuccess =
        row.data.isExecutionSuccessful !== undefined
          ? Boolean(row.data.isExecutionSuccessful).toString()
          : ''
    }

    if (row.category === AuditCategory.TSPAccounts) {
      actionData.name = row?.data?.name ?? row.entityName
      actionData.tspId = String(row.entityId)

      if (row.action === AuditAction.AddressChanged) {
        const oldAddres = row.data.oldValue ? formatAddress(row.data.oldValue) : '---'
        const newAddres = row.data.newValue ? formatAddress(row.data.newValue) : '---'

        actionData.oldValue = oldAddres.trim() !== '' ? oldAddres : '---'
        actionData.newValue = newAddres.trim() !== '' ? newAddres : '---'
      }

      if (row.action === AuditAction.FieldChanged && row.data.field === 'phone') {
        actionData.oldValue = row.data.oldValue ? formatPhoneNumber(row.data.oldValue) : '---'
        actionData.newValue = row.data.newValue ? formatPhoneNumber(row.data.newValue) : '---'
      }

      if (row.action === AuditAction.FieldChanged && row.data.field === 'pricingPlanId') {
        actionData.oldValue =
          typeof parseInt(row.data.oldValue) === 'number'
            ? await this.languageService.translate(`common.pricing-plans.${row.data.oldValue}`)
            : '---'
        actionData.newValue =
          typeof parseInt(row.data.newValue) === 'number'
            ? await this.languageService.translate(`common.pricing-plans.${row.data.newValue}`)
            : '---'
      }

      if (row.action === AuditAction.FieldChanged && row.data.field === 'isDemo') {
        actionData.oldValue = row.data.oldValue
          ? await this.languageService.translate('audit-log.common.demo')
          : await this.languageService.translate('audit-log.common.non-demo')
        actionData.newValue = row.data.newValue
          ? await this.languageService.translate('audit-log.common.demo')
          : await this.languageService.translate('audit-log.common.non-demo')
      }
    }

    // for old adit logs without customSensorName in data
    if (row.category === AuditCategory.CustomSensors) {
      actionData.customSensorName = row.data.customSensorName ?? row.entityName
    }

    if (
      row.category === AuditCategory.SystemControls &&
      (row.action === AuditAction.MaintenanceEnabled ||
        row.action === AuditAction.MaintenanceDisabled)
    ) {
      actionData.app = await this.languageService.translate(
        `audit-log.actions.system-controls.apps.${row.data.app}`,
      )
    }
    if (
      row.category === AuditCategory.SystemControls &&
      row.action === AuditAction.DevUtilExecuted
    ) {
      actionData.command = await this.languageService.translate(
        `audit-log.actions.system-controls.dev-utils.${row.data.command}`,
      )
    }

    return actionData
  }

  public override async loadPage(props: DataQueryProps): Promise<AuditLog[]> {
    return this.api.ti.auditLogs.list(props)
  }

  public override export$(
    format: ExportFormat,
    fileName: string,
    params: DataQueryProps,
  ): Observable<void> {
    return this.api.ti.auditLogs.export$(format, fileName, params, 'ti-admin')
  }

  public override async getAllUsers(): Promise<UserProfile[]> {
    const users = this.usersStore.view(undefined)
    await users.revalidateIfStale()
    return [...users.getAll()]
  }
}
